import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { getLocalizedString } from "@icarius-localization/strings";
import { CloseIcon } from "@icarius-icons";
import { Grid, DialogContent, DialogActions, DialogTitle, Dialog } from "@material-ui/core";
import DialogTitleDivider from "@icarius-common/dialogTitleDivider";
import ButtonDialogAction from "@icarius-common/buttonDialogAction";
import DialogTransition from "@icarius-common/dialogTransition";
import PaperDraggable from "@icarius-common/paperDraggable";
import { openSnackbarAction } from "@icarius-common/snackbar/actions";
import useHandleForm from "./useHandleFormCreateAiAssisted";
import {
    generateQueryDefinitionAction,
    implementQueryDefinitionAction,
    testQueryDefinitionAction,
} from "../../actions";
import {
    getActiveTypes,
    getDataTypes,
    getRoleTypes,
    getListTypes,
    getParamTypes,
    getRequiredTypes,
} from "../../selectors";
import GenerationDataPanel from "./aiAssisted/generationDataPanel";
import BaseFields from "./aiAssisted/baseFields";
import GeneratedFields from "./aiAssisted/generatedFields";
import QuestionsSection from "./aiAssisted/questionsSection";
import ParametersSection from "./aiAssisted/parametersSection";
import TestResultsSection from "./aiAssisted/testResultsSection";

const CreateOrUpdateAiAssistedDialog = (props) => {
    const { open, data, handleClose } = props;

    const dispatch = useDispatch();

    const activeTypes = useSelector(getActiveTypes);
    const roleTypes = useSelector(getRoleTypes);
    const dataTypes = useSelector(getDataTypes);
    const listTypes = useSelector(getListTypes);
    const paramTypes = useSelector(getParamTypes);
    const requiredTypes = useSelector(getRequiredTypes);

    const openValidationError = () => {
        dispatch(openSnackbarAction({ msg: getLocalizedString("invalidData"), severity: "error", duration: 10000 }));
    };

    const openSelectorValidationError = () => {
        dispatch(
            openSnackbarAction({ msg: getLocalizedString("invalidSelectors"), severity: "error", duration: 10000 })
        );
    };

    const openMandatoryParametersError = () => {
        dispatch(
            openSnackbarAction({
                msg: getLocalizedString("mustEnterMandatoryParameters"),
                severity: "error",
                duration: 10000,
            })
        );
    };

    const generate = (payload) => dispatch(generateQueryDefinitionAction(payload));

    const implement = (payload) => {
        dispatch(implementQueryDefinitionAction(payload)).then((res) => {
            if (res?.status === 200) handleClose();
            return res;
        });
    };

    const test = (payload) => dispatch(testQueryDefinitionAction(payload));

    const {
        isCreate,
        formData,
        setFormValue,
        generate: handleGenerate,
        implement: handleImplement,
        test: handleTest,
        hasQueryContent,
        summary,
        summaryIsError,
        warnings,
        promptHistory,
        hasTested,
        testFilters,
        testResult,
        isTesting,
    } = useHandleForm(
        data,
        generate,
        implement,
        dataTypes,
        openValidationError,
        openSelectorValidationError,
        test,
        openMandatoryParametersError
    );

    return (
        <Dialog
            TransitionComponent={DialogTransition}
            PaperComponent={PaperDraggable}
            open={open}
            scroll={"paper"}
            fullWidth
            maxWidth={"lg"}
        >
            <DialogTitle style={{ cursor: "move" }} id="draggable-dialog-title">
                {getLocalizedString(isCreate ? "generateQueryWithAI" : "updateQueryWithAI")}
                <DialogTitleDivider />
            </DialogTitle>
            <CloseIcon className={"dialog-close-icon icon"} onClick={handleClose} />
            <DialogContent style={{ paddingTop: 0 }}>
                <GenerationDataPanel summary={summary} summaryIsError={summaryIsError} />
                <GenerationDataPanel warnings={warnings} />
                <Grid container item xs={12}>
                    <Grid container item xs={12} spacing={2}>
                        <Grid container item xs={12}>
                            <BaseFields
                                formData={formData}
                                setFormValue={setFormValue}
                                isCreate={isCreate}
                                activeTypes={activeTypes}
                                roleTypes={roleTypes}
                                promptHistory={promptHistory}
                            />
                        </Grid>
                        {hasQueryContent && (
                            <Grid container item xs={12}>
                                <GeneratedFields
                                    formData={formData}
                                    setFormValue={setFormValue}
                                    hasGenerated={hasQueryContent}
                                    dataTypes={dataTypes}
                                />
                            </Grid>
                        )}
                        {Boolean(formData.questions) && (
                            <Grid container item xs={12}>
                                <QuestionsSection
                                    questions={formData.questions}
                                    onChange={(questions) => setFormValue(questions, "questions")}
                                    disabled={!hasQueryContent}
                                />
                            </Grid>
                        )}
                        {hasQueryContent && (
                            <Grid container item xs={12}>
                                <ParametersSection
                                    parameters={formData.parameters}
                                    onChange={(parameters) => setFormValue(parameters, "parameters")}
                                    hasGenerated={hasQueryContent}
                                    requiredTypes={requiredTypes}
                                    listTypes={listTypes}
                                    paramTypes={paramTypes}
                                />
                            </Grid>
                        )}
                        {hasQueryContent && (hasTested || isTesting) && (
                            <Grid container item xs={12}>
                                <TestResultsSection filters={testFilters} result={testResult} isTesting={isTesting} />
                            </Grid>
                        )}
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <ButtonDialogAction onClick={handleClose} text={getLocalizedString("cancel")} />
                <ButtonDialogAction
                    onClick={handleGenerate}
                    text={getLocalizedString(isCreate ? "generateQueryWithAI" : "updateQueryWithAI")}
                />
                <ButtonDialogAction
                    disabled={!hasQueryContent || isTesting}
                    onClick={handleTest}
                    text={getLocalizedString("testQuery")}
                />
                <ButtonDialogAction
                    disabled={!hasQueryContent}
                    onClick={handleImplement}
                    isAccept
                    text={getLocalizedString("implementQuery")}
                />
            </DialogActions>
        </Dialog>
    );
};

export default CreateOrUpdateAiAssistedDialog;
